import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Field, ColorPicker } from '../../components/ui/Field';
import { useFormErrors } from '../../lib/useFormErrors';
import { crewMutations, employeeMutations, useCrews, useEmployees } from '../../lib/queries';
import { useToast } from '../../components/ui/Toast';
import { ApiError } from '../../lib/api';
import { PALETTE } from '../../lib/labels';
import type { Crew, Employee } from '../../lib/types';

type FormState = Omit<Crew, 'id'>;

interface Props {
  crew: Crew | null;
  onClose: () => void;
}

/**
 * Kolonne anlegen oder bearbeiten — samt Besetzung. Die Mitarbeiter-Liste
 * nutzt dieselbe Ansicht wie der Einteilen-Dialog im Schichtplan (sdd-*):
 * anhaken heißt zuordnen, abhaken heißt herausnehmen. Beim Neuanlegen bleibt
 * der Dialog nach dem Speichern offen, damit direkt befüllt werden kann.
 */
export function CrewDialog({ crew, onClose }: Props) {
  // Nach dem Anlegen wird aus dem Neu-Dialog der Bearbeiten-Dialog —
  // deshalb liegt die Kolonne im eigenen Zustand statt nur im Prop.
  const [savedCrew, setSavedCrew] = useState<Crew | null>(crew);
  const isEdit = savedCrew !== null;

  const [form, setForm] = useState<FormState>(
    crew ? { name: crew.name, color: crew.color, sortOrder: crew.sortOrder, active: crew.active } : {
      name: '',
      color: PALETTE[0],
      sortOrder: 0,
      active: true,
    },
  );
  const { errors, capture, clear, fieldError } = useFormErrors();
  const toast = useToast();

  const create = crewMutations.useCreate();
  const update = crewMutations.useUpdate();
  const busy = create.isPending || update.isPending;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    clear();
    if (isEdit) {
      update.mutate(
        { ...form, id: savedCrew.id },
        {
          onSuccess: () => {
            toast.success('Kolonne gespeichert.');
            onClose();
          },
          onError: capture,
        },
      );
    } else {
      create.mutate(form, {
        onSuccess: (created) => {
          setSavedCrew(created as Crew);
          toast.success(`Kolonne "${form.name}" angelegt — jetzt Mitarbeiter zuordnen.`);
        },
        onError: capture,
      });
    }
  };

  return (
    <Modal
      title={isEdit ? 'Kolonne bearbeiten' : 'Neue Kolonne'}
      subtitle="Feste Arbeitsgruppe für die Einteilung"
      onClose={onClose}
      width="voll"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {isEdit ? 'Schließen' : 'Abbrechen'}
          </button>
          <button type="submit" form="crew-form" className="btn btn-signal" disabled={busy}>
            {busy ? 'Wird gespeichert …' : 'Speichern'}
          </button>
        </>
      }
    >
      <form id="crew-form" className="stack" onSubmit={submit} noValidate>
        {errors.general ? (
          <p className="form-error" role="alert">
            {errors.general}
          </p>
        ) : null}

        <div className="form-grid">
          <span className="label form-section">Kolonne</span>

          <Field label="Name" error={fieldError('name')} className="span-2">
            {(props) => (
              <input
                {...props}
                className="input"
                autoFocus={!isEdit}
                placeholder="z. B. Kolonne Nord"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            )}
          </Field>

          <div className="span-2">
            <ColorPicker
              label="Farbe"
              value={form.color}
              onChange={(color) => setForm({ ...form, color })}
            />
          </div>

          <span className="label form-section">Planung</span>

          <Field
            label="Reihenfolge"
            error={fieldError('sortOrder')}
            hint="Kleinere Zahl zuerst"
            className="span-2"
          >
            {(props) => (
              <input
                {...props}
                className="input"
                type="number"
                min={0}
                value={form.sortOrder}
                onChange={(event) => setForm({ ...form, sortOrder: Number(event.target.value) })}
              />
            )}
          </Field>

          <label className="check span-2">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(event) => setForm({ ...form, active: event.target.checked })}
            />
            Aktiv
          </label>

          <span className="label form-section">Mitarbeiter</span>
        </div>
      </form>

      {savedCrew ? (
        <CrewMemberPicker crew={savedCrew} color={form.color} />
      ) : (
        <p className="hint" style={{ marginTop: 'var(--space-2)' }}>
          Erst die Kolonne speichern — danach lassen sich hier Mitarbeiter zuordnen.
        </p>
      )}
    </Modal>
  );
}

/**
 * Besetzung der Kolonne: alle aktiven Mitarbeiter, gegliedert wie im
 * Einteilen-Dialog — diese Kolonne zuerst, dann „Ohne Kolonne" (die übliche
 * Quelle), dann die anderen Kolonnen. Jeder Haken schreibt sofort.
 */
function CrewMemberPicker({ crew, color }: { crew: Crew; color: string }) {
  const toast = useToast();
  const employeesQuery = useEmployees();
  const crewsQuery = useCrews();
  const updateEmployee = employeeMutations.useUpdate();

  const groups = useMemo(() => {
    const employees = (employeesQuery.data ?? []).filter((employee) => employee.active);
    const crews = crewsQuery.data ?? [];

    const members = employees.filter((employee) => employee.crewId === crew.id);
    const withoutCrew = employees.filter(
      (employee) =>
        employee.crewId === null ||
        (employee.crewId !== crew.id && !crews.some((entry) => entry.id === employee.crewId)),
    );
    const others = crews
      .filter((entry) => entry.id !== crew.id)
      .map((entry) => ({
        crew: entry as Crew | null,
        members: employees.filter((employee) => employee.crewId === entry.id),
      }))
      .filter((group) => group.members.length > 0);

    return [
      { crew: crew as Crew | null, members },
      { crew: null, members: withoutCrew },
      ...others,
    ];
  }, [employeesQuery.data, crewsQuery.data, crew]);

  /** Zuordnen oder herausnehmen — über den bestehenden Mitarbeiter-Endpunkt. */
  const toggle = (employee: Employee, join: boolean) => {
    updateEmployee.mutate(
      { ...employee, crewId: join ? crew.id : null },
      {
        onError: (error) =>
          toast.error(error instanceof ApiError ? error.message : 'Zuordnung fehlgeschlagen.'),
      },
    );
  };

  if (employeesQuery.isLoading) {
    return (
      <div className="loading">
        <span className="spinner" aria-hidden="true" />
        Mitarbeiter werden geladen …
      </div>
    );
  }

  return (
    <div className="sdd-groups">
      {groups.map((group) => {
        const isThisCrew = group.crew?.id === crew.id;
        if (!isThisCrew && group.members.length === 0) return null;

        return (
          <section key={group.crew?.id ?? 'ohne'} className="sdd-group">
            <h3
              style={{
                '--crew': isThisCrew ? color : (group.crew?.color ?? 'var(--line-strong)'),
              } as never}
            >
              {isThisCrew
                ? `Diese Kolonne (${group.members.length})`
                : (group.crew?.name ?? 'Ohne Kolonne')}
            </h3>

            {isThisCrew && group.members.length === 0 ? (
              <p className="hint">Noch niemand zugeordnet — unten anhaken.</p>
            ) : (
              <ul className="sdd-list">
                {group.members.map((employee) => {
                  const isMember = employee.crewId === crew.id;
                  return (
                    <li key={employee.id}>
                      <label className={`sdd-item${isMember ? ' is-on' : ''}`}>
                        <input
                          type="checkbox"
                          checked={isMember}
                          disabled={updateEmployee.isPending}
                          onChange={() => toggle(employee, !isMember)}
                        />
                        <span className="sdd-item-name">{employee.displayName}</span>
                        {employee.role ? (
                          <span className="sdd-item-role">{employee.role}</span>
                        ) : null}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
